/**
 *
 * @module serviceDefinitionsConfiguration
 *
 */

import Check from 'check-types';
import Logging from './../../logging/entry';
import Util from './../util/entry';

import AccountClientConfiguration from './../account/accountClientConfiguration';
import AccountManagerConfiguration from './accountManagerConfiguration';
import AdEngineManagerConfiguration from './adEngineManagerConfiguration';
import AdEngineClientConfiguration from './../media/adEngine/adEngineClientConfiguration';
import AdEngineManagerExtrasMap from './adEngineManagerExtrasMap';
import AdobeIdentityClientConfiguration from './../identity/adobe/adobeIdentityClientConfiguration';
import AdobeIdentityManagerConfiguration from './adobeIdentityManagerConfiguration';
import BamIdentityClientConfiguration from './../identity/bam/bamIdentityClientConfiguration';
import BamIdentityManagerConfiguration from './bamIdentityManagerConfiguration';
import CommerceClientConfiguration from './../commerce/commerceClientConfiguration';
import CommerceClientExtrasMap from './../commerce/commerceClientExtrasMap';
import CommerceManagerConfiguration from './commerceManagerConfiguration';
import CommerceManagerExtrasMap from './commerceManagerExtrasMap';
import ContentClientConfiguration from './../content/contentClientConfiguration';
import ContentClientExtrasMap from './../content/contentClientExtrasMap';
import ContentManagerConfiguration from './contentManagerConfiguration';
import CustomerServiceClientConfiguration from './../customerService/customerServiceClientConfiguration';
import CustomerServiceManagerConfiguration from './customerServiceManagerConfiguration';
import DeviceClientConfiguration from './../device/deviceClientConfiguration';
import DrmClientConfiguration from './../drm/drmClientConfiguration';
import DeviceManagerConfiguration from './deviceManagerConfiguration';
import DrmManagerConfiguration from './drmManagerConfiguration';
import MediaClientConfiguration from './../media/mediaClientConfiguration';
import MediaManagerConfiguration from './mediaManagerConfiguration';
import MediaManagerExtrasMap from './mediaManagerExtrasMap';
import PaywallManagerConfiguration from './paywallManagerConfiguration';
import PaywallClientConfiguration from './../paywall/paywallClientConfiguration';
import PurchaseClientConfiguration from './../purchase/purchaseClientConfiguration';
import PurchaseManagerConfiguration from './purchaseManagerConfiguration';
import PurchaseManagerExtrasMap from './purchaseManagerExtrasMap';
import SessionClientConfiguration from './../session/sessionClientConfiguration';
import SessionManagerConfiguration from './sessionManagerConfiguration';
import SubscriptionClientConfiguration from './../subscription/subscriptionClientConfiguration';
import SubscriptionManagerConfiguration from './subscriptionManagerConfiguration';
import TelemetryClientConfiguration from './../internal/telemetry/telemetryClientConfiguration';
import TelemetryManagerConfiguration from './telemetryManagerConfiguration';
import TelemetryManagerExtrasMap from './telemetryManagerExtrasMap';
import TokenClientConfiguration from './../token/tokenClientConfiguration';
import TokenClientExtrasMap from './../token/tokenClientExtrasMap';
import TokenManagerConfiguration from './tokenManagerConfiguration';
import TokenManagerExtrasMap from './tokenManagerExtrasMap';

import RetryPolicy from './retryPolicy';
import ServiceEndpoint from './serviceEndpoint';

import InvalidArgumentException from './../exception/invalidArgumentException';

const {
    nonEmptyObject
} = Check.assert;

/**
 *
 * @class ServiceDefinitionsConfiguration
 * @desc Provides configuration information necessary to communicate with services.
 *
 */
export default class ServiceDefinitionsConfiguration {

    /**
     *
     * @param {Object} services
     * @param {Object} [commonHeaders={}]
     * @param {SDK.Logging.Logger} [logger=Logging]
     *
     */
    constructor(services, commonHeaders, logger = Logging) {

        const errorMessage = {
            ctor: 'ServiceDefinitionsConfiguration(services, commonHeaders, logger)',
            get services() {
                return `${this.ctor} services should be a plain non-empty Object`;
            }
        };

        nonEmptyObject(
            services,
            errorMessage.services,
            InvalidArgumentException
        );

        /**
         *
         * @access private
         * @type {Object}
         *
         */
        this.commonHeaders = commonHeaders;

        /**
         *
         * @access public
         * @type {SDK.Services.Configuration.AccountManagerConfiguration}
         * @desc Gets or sets the configuration information necessary for managing accounts.
         *
         */
        this.account = {};

        /**
         *
         * @access public
         * @type {SDK.Services.Configuration.AdEngineConfiguration}
         * @desc Gets or sets the configuration information necessary for managing adEngine.
         *
         */
        this.adEngine = {};

        /**
         *
         * @access public
         * @type {SDK.Services.Configuration.CustomerServiceManagerConfiguration}
         * @desc Gets or sets the configuration information necessary for managing customer service.
         *
         */
        this.customerService = {};

        /**
         *
         * @access public
         * @type {SDK.Services.Configuration.DeviceManagerConfiguration}
         * @desc Gets or sets the configuration information necessary for managing device identity.
         *
         */
        this.device = {};

        /**
         *
         * @access public
         * @type {SDK.Services.Configuration.DrmManagerConfiguration}
         * @desc Gets or sets the configuration information necessary for managing Drm capabilities.
         *
         */
        this.drm = {};

        /**
         *
         * @access public
         * @type {SDK.Services.Configuration.AdobeIdentityManagerConfiguration}
         * @desc Gets or sets the configuration information necessary for managing MVPD authentication.
         *
         */
        this.adobeIdentity = {};

        /**
         *
         * @access public
         * @type {SDK.Services.Configuration.BamIdentityManagerConfiguration}
         * @desc Gets or sets the configuration information necessary for managing BAM authentication.
         *
         */
        this.bamIdentity = {};

        /**
         *
         * @access public
         * @type {SDK.Services.Configuration.PurchaseManagerConfiguration}
         * @desc Gets or sets the configuration information necessary for managing receipt activation.
         *
         */
        this.purchase = {};

        /**
         *
         * @access public
         * @type {SDK.Services.Configuration.SessionManagerConfiguration}
         * @desc Gets or sets the configuration information necessary for managing session.
         *
         */
        this.session = {};

        /**
         *
         * @access public
         * @type {SDK.Services.Configuration.SubscriptionManagerConfiguration}
         * @desc Gets or sets the configuration information necessary for managing subscriptions.
         *
         */
        this.subscription = {};

        /**
         *
         * @access public
         * @type {SDK.Services.Configuration.TokenManagerConfiguration}
         * @desc Gets or sets the configuration information necessary for managing token exchange.
         *
         */
        this.token = {};

        /**
         *
         * @access public
         * @type {SDK.Services.Configuration.MediaManagerConfiguration}
         * @desc Gets or sets the configuration information necessary for managing media.
         *
         */
        this.media = {};

        /**
         *
         * @access public
         * @type {SDK.Services.Configuration.TelemetryManagerConfiguration}
         * @desc Gets or sets the configuration information necessary for managing telemetry.
         *
         */
        this.telemetry = {};

        /**
         *
         * @access public
         * @type {SDK.Services.Configuration.ContentManagerConfiguration}
         * @desc Gets or sets the configuration information necessary for managing content data.
         *
         */
        this.content = {};

        /**
         *
         * @access public
         * @type {SDK.Services.Configuration.CommerceManagerConfiguration}
         * @desc Gets or sets the configuration information necessary for managing commerce.
         *
         */
        this.commerce = {};

        /**
         *
         * @access public
         * @type {SDK.Services.Configuration.PaywallManagerConfiguration}
         * @desc Gets or sets the configuration information necessary for managing paywall.
         *
         */
        this.paywall = {};

        /**
         *
         * @desc process services Object and create configuration instances
         *
         */
        this.processServices(services, logger);

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {Object} services - JSON services config object returned from configuration service.
     * @param {SDK.Logging.Logger} logger
     * @desc Initialize SdkSessionConfiguration services.
     *
     */
    processServices(services, logger) {

        const { account, adEngine, customerService, device, drm, adobeIdentity, bamIdentity, purchase } = services;
        const { session, subscription, token, media, telemetry, content, commerce, paywall } = services;

        const { client: contentClient } = content;
        const { endpoints: contentClientEndpoints, extras: contentClientExtras } = contentClient;

        const { extras: adEngineExtras } = adEngine;
        const { adTargeting } = adEngineExtras;

        const { client: commerceClient, extras: commerceExtras } = commerce;
        const { endpoints: commerceClientEndpoints, extras: commerceClientExtras } = commerceClient;
        const { retryPolicy: commerceAutoRefreshRetryPolicy, checkOrderStatusDelay } = commerceExtras;

        const { client: tokenClient, extras: tokenExtras } = token;
        const { endpoints: tokenClientEndpoints, extras: tokenClientExtras } = tokenClient;
        const { autoRefreshRetryPolicy: tokenAutoRefreshRetryPolicy } = tokenExtras;

        const { extras: mediaExtras } = media;
        const { extras: purchaseExtras } = purchase;
        const { retryPolicy: purchaseAutoRefreshRetryPolicy } = purchaseExtras;
        const { extras: telemetryExtras } = telemetry;

        const purchaseRetryPolicy = new RetryPolicy(
            purchaseAutoRefreshRetryPolicy.retryBasePeriod,
            purchaseAutoRefreshRetryPolicy.retryMultiplier,
            purchaseAutoRefreshRetryPolicy.retryMaxAttempts,
            purchaseAutoRefreshRetryPolicy.retryMaxPeriod,
            logger
        );

        const tokenRetryPolicy = new RetryPolicy(
            tokenAutoRefreshRetryPolicy.retryBasePeriod,
            tokenAutoRefreshRetryPolicy.retryMultiplier,
            tokenAutoRefreshRetryPolicy.retryMaxAttempts,
            tokenAutoRefreshRetryPolicy.retryMaxPeriod,
            logger
        );

        const commerceRetryPolicy = new RetryPolicy(
            commerceAutoRefreshRetryPolicy.retryBasePeriod,
            commerceAutoRefreshRetryPolicy.retryMultiplier,
            commerceAutoRefreshRetryPolicy.retryMaxAttempts,
            commerceAutoRefreshRetryPolicy.retryMaxPeriod,
            logger
        );

        /**
         *
         * @type {SDK.Services.Account.AccountClientConfiguration}
         *
         */
        const accountClientConfiguration = new AccountClientConfiguration(
            Util.getSafe(() => account.client.baseUrl),
            this.processEndpoints(Util.getSafe(() => account.client.endpoints))
        );

        /**
         *
         * @type {SDK.Services.Media.AdEngine.AdEngineClientConfiguration}
         *
         */
        const adEngineClientConfiguration = new AdEngineClientConfiguration(
            Util.getSafe(() => adEngine.client.baseUrl),
            this.processEndpoints(Util.getSafe(() => adEngine.client.endpoints))
        );

        /**
         *
         * @type {SDK.Services.Identity.Adobe.AdobeIdentityClientConfiguration}
         *
         */
        const adobeIdentityClientConfiguration = new AdobeIdentityClientConfiguration(
            Util.getSafe(() => adobeIdentity.client.baseUrl),
            this.processEndpoints(Util.getSafe(() => adobeIdentity.client.endpoints))
        );

        /**
         *
         * @type {SDK.Services.Identity.Bam.BamIdentityClientConfiguration}
         *
         */
        const bamIdentityClientConfiguration = new BamIdentityClientConfiguration(
            Util.getSafe(() => bamIdentity.client.baseUrl),
            this.processEndpoints(Util.getSafe(() => bamIdentity.client.endpoints))
        );

        /**
         *
         * @type {SDK.Services.Content.ContentClientConfiguration}
         *
         */
        const contentClientConfiguration = new ContentClientConfiguration(
            contentClient.baseUrl,
            this.processEndpoints(contentClientEndpoints),
            new ContentClientExtrasMap(contentClientExtras.urlSizeLimit)
        );

        /**
         *
         * @type {SDK.Services.CustomerService.CustomerServiceClientConfiguration}
         *
         */
        const customerServiceClientConfiguration = new CustomerServiceClientConfiguration(
            Util.getSafe(() => customerService.client.baseUrl),
            this.processEndpoints(Util.getSafe(() => customerService.client.endpoints))
        );

        /**
         *
         * @type {SDK.Services.Device.DeviceClientConfiguration}
         *
         */
        const deviceClientConfiguration = new DeviceClientConfiguration(
            Util.getSafe(() => device.client.baseUrl),
            this.processEndpoints(Util.getSafe(() => device.client.endpoints))
        );

        /**
         *
         * @type {SDK.Services.Drm.DrmClientConfiguration}
         *
         */
        const drmClientConfiguration = new DrmClientConfiguration(
            Util.getSafe(() => drm.client.baseUrl),
            this.processEndpoints(Util.getSafe(() => drm.client.endpoints))
        );

        /**
         *
         * @type {SDK.Services.Media.MediaClientConfiguration}
         *
         */
        const mediaClientConfiguration = new MediaClientConfiguration(
            Util.getSafe(() => media.client.baseUrl),
            this.processEndpoints(Util.getSafe(() => media.client.endpoints))
        );

        /**
         *
         * @type {SDK.Services.Purchase.PurchaseClientConfiguration}
         *
         */
        const purchaseClientConfiguration = new PurchaseClientConfiguration(
            Util.getSafe(() => purchase.client.baseUrl),
            this.processEndpoints(Util.getSafe(() => purchase.client.endpoints))
        );

        /**
         *
         * @type {SDK.Services.Session.SessionClientConfiguration}
         *
         */
        const sessionClientConfiguration = new SessionClientConfiguration(
            Util.getSafe(() => session.client.baseUrl),
            this.processEndpoints(Util.getSafe(() => session.client.endpoints))
        );

        /**
         *
         * @type {SDK.Services.Subscription.SubscriptionClientConfiguration}
         *
         */
        const subscriptionClientConfiguration = new SubscriptionClientConfiguration(
            Util.getSafe(() => subscription.client.baseUrl),
            this.processEndpoints(Util.getSafe(() => subscription.client.endpoints))
        );

        /**
         *
         * @type {SDK.Services.Internal.Telemetry.TelemetryClientConfiguration}
         *
         */
        const telemetryClientConfiguration = new TelemetryClientConfiguration(
            Util.getSafe(() => telemetry.client.baseUrl),
            this.processEndpoints(Util.getSafe(() => telemetry.client.endpoints))
        );

        /**
         *
         * @type {SDK.Services.Token.TokenClientConfiguration}
         *
         */
        const tokenClientConfiguration = new TokenClientConfiguration(
            tokenClient.baseUrl,
            this.processEndpoints(tokenClientEndpoints),
            new TokenClientExtrasMap(
                tokenClientExtras.platformId
            )
        );

        /**
         *
         * @type {SDK.Services.Commerce.CommerceClientConfiguration}
         *
         */
        const commerceClientConfiguration = new CommerceClientConfiguration(
            commerceClient.baseUrl,
            this.processEndpoints(commerceClientEndpoints),
            new CommerceClientExtrasMap(
                Util.getSafe(() => commerceClientExtras.namespace),
                Util.getSafe(() => commerceClientExtras.clientId),
                Util.getSafe(() => commerceClientExtras.appAccount),
                Util.getSafe(() => commerceClientExtras.createPaymentMethodRegionalEndpoints)
            )
        );

        /**
         *
         * @type {SDK.Services.Paywall.PaywallClientConfiguration}
         *
         */
        const paywallClientConfiguration = new PaywallClientConfiguration(
            Util.getSafe(() => paywall.client.baseUrl),
            this.processEndpoints(paywall.client.endpoints)
        );

        /**
         *
         * @type {SDK.Services.Configuration.AccountManagerConfiguration}
         *
         */
        this.account = new AccountManagerConfiguration(
            accountClientConfiguration,
            null,
            account.disabled
        );

        /**
         *
         * @type {SDK.Services.Configuration.AdobeIdentityManagerConfiguration}
         *
         */
        this.adobeIdentity = new AdobeIdentityManagerConfiguration(
            adobeIdentityClientConfiguration,
            null,
            adobeIdentity.disabled
        );

        /**
         *
         * @type {SDK.Services.Configuration.AdEngineManagerConfiguration}
         *
         */
        this.adEngine = new AdEngineManagerConfiguration(
            adEngineClientConfiguration,
            new AdEngineManagerExtrasMap(
                adTargeting,
                logger
            ),
            adEngine.disabled
        );

        /**
         *
         * @type {SDK.Services.Configuration.BamIdentityManagerConfiguration}
         *
         */
        this.bamIdentity = new BamIdentityManagerConfiguration(
            bamIdentityClientConfiguration,
            null,
            bamIdentity.disabled
        );

        /**
         *
         * @type {SDK.Services.Configuration.ContentManagerConfiguration}
         *
         */
        this.commerce = new CommerceManagerConfiguration(
            commerceClientConfiguration,
            new CommerceManagerExtrasMap(
                commerceRetryPolicy,
                checkOrderStatusDelay,
                logger
            ),
            commerce.disabled
        );

        /**
         *
         * @type {SDK.Services.Configuration.ContentManagerConfiguration}
         *
         */
        this.content = new ContentManagerConfiguration(
            contentClientConfiguration,
            null,
            content.disabled
        );

        /**
         *
         * @type {SDK.Services.Configuration.CustomerServiceManagerConfiguration}
         *
         */
        this.customerService = new CustomerServiceManagerConfiguration(
            customerServiceClientConfiguration,
            null,
            customerService.disabled
        );

        /**
         *
         * @type {SDK.Services.Configuration.DeviceManagerConfiguration}
         *
         */
        this.device = new DeviceManagerConfiguration(
            deviceClientConfiguration,
            null,
            device.disabled
        );

        /**
         *
         * @type {SDK.Services.Configuration.Drm}
         *
         */
        this.drm = new DrmManagerConfiguration(
            drmClientConfiguration,
            null,
            drm.disabled
        );

        /**
         *
         * @type {SDK.Services.Configuration.MediaManagerConfiguration}
         *
         */
        this.media = new MediaManagerConfiguration(
            mediaClientConfiguration,
            new MediaManagerExtrasMap(
                mediaExtras.playbackSession,
                mediaExtras.playbackScenarioDefault,
                mediaExtras.cookieEnabled,
                logger
            ),
            media.disabled
        );

        /**
         *
         * @type {SDK.Services.Configuration.PurchaseManagerConfiguration}
         *
         */
        this.purchase = new PurchaseManagerConfiguration(
            purchaseClientConfiguration,
            new PurchaseManagerExtrasMap(
                purchaseRetryPolicy,
                logger
            ),
            purchase.disabled
        );

        /**
         *
         * @type {SDK.Services.Configuration.SessionManagerConfiguration}
         *
         */
        this.session = new SessionManagerConfiguration(
            sessionClientConfiguration,
            null,
            session.disabled
        );

        /**
         *
         * @type {SDK.Services.Configuration.SubscriptionManagerConfiguration}
         *
         */
        this.subscription = new SubscriptionManagerConfiguration(
            subscriptionClientConfiguration,
            null,
            subscription.disabled
        );

        /**
         *
         * @type {SDK.Services.Configuration.TelemetryManagerConfiguration}
         *
         */
        this.telemetry = new TelemetryManagerConfiguration(
            telemetryClientConfiguration,
            new TelemetryManagerExtrasMap({
                batchLimit: telemetryExtras.batchLimit,
                replyAfterFallback: telemetryExtras.replyAfterFallback,
                minimumBatchSize: telemetryExtras.minimumBatchSize,
                bufferConfigurationDefault: telemetryExtras.bufferConfigurationDefault,
                streamSampleConfiguration: telemetryExtras.streamSampleConfiguration,
                logger
            }),
            telemetry.disabled
        );

        /**
         *
         * @type {SDK.Services.Configuration.TokenManagerConfiguration}
         *
         */
        this.token = new TokenManagerConfiguration(
            tokenClientConfiguration,
            new TokenManagerExtrasMap(
                tokenExtras.refreshThreshold,
                tokenRetryPolicy,
                tokenExtras.subjectTokenTypes,
                logger
            ),
            token.disabled
        );

        /**
         *
         * @type {SDK.Services.Configuration.PaywallManagerConfiguration}
         *
         */
        this.paywall = new PaywallManagerConfiguration(
            paywallClientConfiguration,
            null,
            paywall.disabled
        );

    }

    /**
     *
     * @access private
     * @param {Object} [endpoints={}]
     * @desc creates service endpoint instance for each item
     * @returns {Object}
     *
     */
    processEndpoints(endpoints = {}) {

        const { commonHeaders } = this;
        const serviceEndpoints = {};

        Object.keys(endpoints).forEach((key) => {

            const endpoint = endpoints[key];

            Object.assign(endpoint.headers, commonHeaders);

            endpoint.rel = key;

            serviceEndpoints[endpoint.rel] = new ServiceEndpoint(
                endpoint.rel,
                endpoint.href,
                endpoint.ttl,
                endpoint.timeout,
                endpoint.templated,
                endpoint.method,
                endpoint.headers
            );

        });

        return serviceEndpoints;

    }

}



// WEBPACK FOOTER //
// ./src/services/configuration/serviceDefinitionsConfiguration.js